import React, { useMemo, useState } from 'react';

import { Eye, EyeOff } from 'lucide-react';

import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';

import { IconKey } from '../../../types/system/icons';

interface SecretFieldProps {
  label: string;
  name: string;

  /**
   * Valor REAL
   *
   * Ej:
   * abcdefghijklmnop
   */
  value?: string;

  /**
   * Devuelve valor limpio
   *
   * Ej:
   * abcdefghijklmnop
   */
  onChange?: (value: string) => void;

  error?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;

  iconKey?: IconKey;

  /**
   * Cantidad de caracteres por grupo
   *
   * abcdefghijklmnop
   *
   * 4 =>
   * abcd efgh ijkl mnop
   */
  groupSize?: number;
}

const SecretField: React.FC<SecretFieldProps> = ({
  label,
  name,
  value = '',
  onChange,
  error,
  placeholder,
  disabled,
  required,
  iconKey,
  groupSize = 4,
}) => {
  const [visible, setVisible] = useState(false);

  // =====================================================
  // 🔹 Formato visual
  // =====================================================

  const displayValue = useMemo(() => {
    const clean = value.replace(/\s/g, '');

    const regex = new RegExp(`.{1,${groupSize}}`, 'g');

    return clean.match(regex)?.join(' ') ?? '';
  }, [value, groupSize]);

  // =====================================================
  // 🔹 Change
  // =====================================================

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\s/g, '');

    onChange?.(rawValue);
  };

  // =====================================================
  // 🔹 Classes
  // =====================================================

  const baseClasses =
    'w-full bg-transparent px-4 py-3.5 text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark2';

  return (
    <div className="mb-5 w-full">
      {/* Label */}
      <label
        htmlFor={name}
        className="
          mb-2.5
          flex
          items-center
          gap-1
          text-sm
          font-medium
          text-black
          dark:text-white
        "
      >
        {label}

        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      {/* Field */}
      <div
        className={`
          bg-gray-1
          flex
          min-w-[180px]
          overflow-hidden
          rounded-xl
          border
          border-stroke
          transition-all
          duration-200
          focus-within:border-primary
          focus-within:ring-2
          focus-within:ring-primary/10
          dark:border-strokedark
          dark:bg-meta-4
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {/* Icon */}
        {iconKey && (
          <div
            className="
              flex
              items-center
              justify-center
              border-r
              border-stroke
              bg-gray-2
              px-4
              dark:border-strokedark
              dark:bg-boxdark-2
            "
          >
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        {/* Input */}
        <input
          id={name}
          type={visible ? 'text' : 'password'}
          name={name}
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          className={baseClasses}
        />

        {/* Toggle */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          className="
            flex
            shrink-0
            items-center
            justify-center
            border-l
            border-stroke
            bg-gray-2
            px-4
            text-body
            transition
            hover:text-primary
            disabled:cursor-not-allowed
            dark:border-strokedark
            dark:bg-boxdark-2
            dark:text-bodydark2
            dark:hover:text-primary
          "
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {/* Error */}
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default SecretField;
